import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useBusinessCategories } from "../../../hooks/useBusinessCatalog";

const CategoryRuleCheckbox = ({ checked, label, onToggle, variant }) => {
  const isChecked = Boolean(checked);
  const handleToggle = () => {
    if (!onToggle) {
      return;
    }
    onToggle();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    handleToggle();
  };

  const baseColorClasses =
    variant === "exclude"
      ? "border-red-500/50 data-[checked=true]:bg-red-500 data-[checked=true]:border-red-500"
      : "border-blue-500/50 data-[checked=true]:bg-blue-500 data-[checked=true]:border-blue-500";

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isChecked}
      aria-label={label}
      tabIndex={0}
      data-checked={isChecked}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-gray-950 transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none ${baseColorClasses}`}
    >
      {isChecked ? <Check className="h-3.5 w-3.5 text-white" /> : null}
    </button>
  );
};

const normalizeRulesCategoriesValue = (value) => {
  if (!Array.isArray(value)) {
    return new Map();
  }

  const map = new Map();

  value.forEach((item) => {
    if (typeof item === "number") {
      map.set(item, true);
      return;
    }

    if (!item || typeof item !== "object") {
      return;
    }

    const id = Number(item.id);
    if (!Number.isFinite(id)) {
      return;
    }

    const includeFromItem =
      typeof item.include === "boolean"
        ? item.include
        : typeof item?.pivot?.include === "boolean"
          ? item.pivot.include
          : true;

    map.set(id, includeFromItem);
  });

  return map;
};

const buildRulesCategoriesPayload = (selectionMap) => {
  const entries = Array.from(selectionMap.entries());

  if (entries.length === 0) {
    return null;
  }

  return entries.map(([id, include]) => ({
    id,
    include,
    is_condition: true,
  }));
};

const buildCategoryTree = (categories) => {
  const safe = Array.isArray(categories) ? categories : [];

  const toNode = (category) => {
    const id = Number(category?.id);
    const enabled = category?.enabled;

    if (!Number.isFinite(id)) {
      return null;
    }

    const children = Array.isArray(category?.subcategories)
      ? category.subcategories.map(toNode).filter(Boolean)
      : [];

    return {
      id,
      name: String(category?.name || `Category ${id}`),
      enabled: enabled !== false,
      children,
    };
  };

  return safe.map(toNode).filter(Boolean);
};

const collectAllNodeIds = (nodes, set) => {
  nodes.forEach((node) => {
    set.add(node.id);
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectAllNodeIds(node.children, set);
    }
  });
};

const filterTreeBySearch = (nodes, query) => {
  if (!query) {
    return nodes;
  }

  const lower = query.toLowerCase();

  const filterNode = (node) => {
    const nameMatch = node.name.toLowerCase().includes(lower);
    const filteredChildren = node.children.map(filterNode).filter(Boolean);

    if (nameMatch || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    return null;
  };

  return nodes.map(filterNode).filter(Boolean);
};

const flattenTree = (nodes, expandedSet, depth = 0) => {
  const result = [];

  nodes.forEach((node) => {
    result.push({ node, depth });

    if (node.children.length === 0) {
      return;
    }

    if (!expandedSet.has(node.id)) {
      return;
    }

    result.push(...flattenTree(node.children, expandedSet, depth + 1));
  });

  return result;
};

const PromotionRulesCategories = ({
  selectedBusinessIds,
  businesses,
  value,
  onChange,
}) => {
  const eligibleBusinesses = useMemo(() => {
    const ids = new Set(
      Array.isArray(selectedBusinessIds)
        ? selectedBusinessIds.map((id) => Number(id))
        : [],
    );

    const list = Array.isArray(businesses) ? businesses : [];

    return list
      .filter((business) => ids.has(Number(business?.id)))
      .map((business) => ({
        id: Number(business.id),
        name: String(business.name || `Business ${business.id}`),
      }))
      .filter((business) => Number.isFinite(business.id));
  }, [businesses, selectedBusinessIds]);

  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (eligibleBusinesses.length === 0) {
      setSelectedBusinessId("");
      return;
    }

    if (
      eligibleBusinesses.some(
        (b) => String(b.id) === String(selectedBusinessId),
      )
    ) {
      return;
    }

    setSelectedBusinessId(String(eligibleBusinesses[0].id));
  }, [eligibleBusinesses, selectedBusinessId]);

  const { data: categoriesData, isLoading } = useBusinessCategories(
    selectedBusinessId,
    {
      enabled: Boolean(selectedBusinessId),
    },
  );

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesData),
    [categoriesData],
  );
  const filteredTree = useMemo(
    () => filterTreeBySearch(categoryTree, search.trim()),
    [categoryTree, search],
  );

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const next = new Set();
    collectAllNodeIds(filteredTree, next);
    setExpandedIds(next);
  }, [selectedBusinessId, search, filteredTree]);

  const selectionMap = useMemo(
    () => normalizeRulesCategoriesValue(value),
    [value],
  );

  const setCategorySelection = (categoryId, nextInclude) => {
    const nextMap = new Map(selectionMap);

    if (nextInclude === null) {
      nextMap.delete(categoryId);
    } else {
      nextMap.set(categoryId, nextInclude);
    }

    const nextPayload = buildRulesCategoriesPayload(nextMap);
    if (onChange) {
      onChange(nextPayload);
    }
  };

  const toggleExpanded = (categoryId) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const rows = useMemo(
    () => flattenTree(filteredTree, expandedIds),
    [filteredTree, expandedIds],
  );

  if (!Array.isArray(selectedBusinessIds) || selectedBusinessIds.length === 0) {
    return (
      <div className="text-sm text-gray-400">
        Select at least one store in the promotion to configure category rules.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-400">
          Business
        </label>
        <div className="relative">
          <select
            value={selectedBusinessId}
            onChange={(event) => setSelectedBusinessId(event.target.value)}
            className="w-full cursor-pointer appearance-none rounded-lg border border-gray-800 bg-gray-950 py-2.5 pr-10 pl-4 text-white transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
            aria-label="Select business for categories"
          >
            {eligibleBusinesses.map((business) => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-400">
            Search categories
          </label>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type to filter…"
            className="w-full rounded-lg border border-gray-800 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
            aria-label="Search categories"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-200">
            Choose category to include
          </h4>
          <div className="max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/60 p-2">
            {isLoading && (
              <p className="p-2 text-xs text-gray-500">Loading categories...</p>
            )}
            {!isLoading && rows.length === 0 && (
              <p className="p-2 text-xs text-gray-500">No categories found.</p>
            )}
            {!isLoading &&
              rows.map(({ node, depth }) => {
                if (!node.enabled) {
                  return null;
                }

                const isSelected = selectionMap.get(node.id) === true;
                const hasChildren = node.children.length > 0;
                const isExpanded = expandedIds.has(node.id);

                return (
                  <div
                    key={`include-${node.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Toggle include ${node.name}`}
                    onClick={(event) => {
                      if (event.target.closest("button")) {
                        return;
                      }
                      setCategorySelection(node.id, isSelected ? null : true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") {
                        return;
                      }
                      event.preventDefault();
                      setCategorySelection(node.id, isSelected ? null : true);
                    }}
                    className="flex min-w-max cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    style={{ paddingLeft: `${8 + depth * 16}px` }}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(node.id)}
                        className="flex h-6 w-6 items-center justify-center rounded text-gray-300 hover:bg-gray-800"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                          node.name
                        }`}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? "" : "-rotate-90"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="w-6" />
                    )}

                    <CategoryRuleCheckbox
                      checked={isSelected}
                      variant="include"
                      label={`Include ${node.name}`}
                      onToggle={() =>
                        setCategorySelection(node.id, isSelected ? null : true)
                      }
                    />
                    <span className="text-sm leading-5 whitespace-nowrap text-gray-200">
                      {node.name}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-200">
            Choose category to exclude
          </h4>
          <div className="max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/60 p-2">
            {isLoading && (
              <p className="p-2 text-xs text-gray-500">Loading categories...</p>
            )}
            {!isLoading && rows.length === 0 && (
              <p className="p-2 text-xs text-gray-500">No categories found.</p>
            )}
            {!isLoading &&
              rows.map(({ node, depth }) => {
                if (!node.enabled) {
                  return null;
                }

                const isSelected = selectionMap.get(node.id) === false;
                const hasChildren = node.children.length > 0;
                const isExpanded = expandedIds.has(node.id);

                return (
                  <div
                    key={`exclude-${node.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Toggle exclude ${node.name}`}
                    onClick={(event) => {
                      if (event.target.closest("button")) {
                        return;
                      }
                      setCategorySelection(node.id, isSelected ? null : false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") {
                        return;
                      }
                      event.preventDefault();
                      setCategorySelection(node.id, isSelected ? null : false);
                    }}
                    className="flex min-w-max cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    style={{ paddingLeft: `${8 + depth * 16}px` }}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(node.id)}
                        className="flex h-6 w-6 items-center justify-center rounded text-gray-300 hover:bg-gray-800"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                          node.name
                        }`}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? "" : "-rotate-90"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="w-6" />
                    )}

                    <CategoryRuleCheckbox
                      checked={isSelected}
                      variant="exclude"
                      label={`Exclude ${node.name}`}
                      onToggle={() =>
                        setCategorySelection(node.id, isSelected ? null : false)
                      }
                    />
                    <span className="text-sm leading-5 whitespace-nowrap text-gray-200">
                      {node.name}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Selecting a category in “Include” sets{" "}
        <span className="text-gray-300">include=true</span>. Selecting in
        “Exclude” sets <span className="text-gray-300">include=false</span>.
        Unchecking removes it from the rule.
      </p>
    </div>
  );
};

export default PromotionRulesCategories;
